import { IBlockedProductDto, IChoiceDto, IModifierDto, IModifierChoiceDto, ISelectedChoice } from "@crunchit/types";

/**
 * Determines if we should ignore the includedChoices property in favor of freeAmount
 * @param modifierChoices
 */
export function ignoreIncludedChoices(modifierChoices: IModifierChoiceDto[]) {
  return modifierChoices.find((m) => m.freeAmount > 0) !== undefined;
}

/**
 * Determines if product is one of blocked products
 * @param productId
 * @param blockedProducts
 */
export function isBlockedProduct(productId: number, blockedProducts: IBlockedProductDto[]) {
  return blockedProducts.find((b: IBlockedProductDto) => b.productId === productId) !== undefined;
}

/**
 * Determines if included choices has been reached
 * @param totalChoiceCount
 * @param choicesIncluded
 * @param modifierChoices
 */
export function hasReachedIncludedChoices(totalChoiceCount: number, choicesIncluded: number, modifierChoices: IModifierChoiceDto[]) {
  return ignoreIncludedChoices(modifierChoices) ? true : totalChoiceCount >= choicesIncluded;
}

/**
 * Determines if total choice count has reach minimum
 * @param totalChoiceCount
 * @param minChoices
 */
export const hasReachedMinChoices = (totalChoiceCount: number, minChoices: number) => {
  return totalChoiceCount >= minChoices;
};

/**
 * Determines if total choice count has reached maximum
 * @param totalChoiceCount
 * @param maxChoices
 */
export function hasReachedMaxChoices(totalChoiceCount: number, maxChoices: number) {
  return maxChoices > 0 ? totalChoiceCount >= maxChoices : false;
}

/**
 * Sums the choice amount in a selected choice list
 * @param choicesList
 */
export function getTotalChoiceCount(choicesList: ISelectedChoice[]) {
  return choicesList.reduce((total: number, current: ISelectedChoice) => total + current.amount, 0);
}

/**
 * Gets the choice price, either the default price or the overwritten price by `modifierChoice`
 * @param choice
 * @param modifierChoice
 */
export function getSingleChoicePrice(defaultPrice: number, overwritePrice: number | null) {
  return overwritePrice === null ? defaultPrice : overwritePrice;
}

/**
 * Gets the total choice price for a modifier
 * @param modifier
 * @param choicesIncluded
 * @param choices
 * @param selectedChoices
 */
export const getTotalChoicePriceForModifier = (modifier: IModifierDto, choicesIncluded: number, choices: IChoiceDto[], selectedChoices: ISelectedChoice[]) => {
  let totalChoicePrice = 0;

  const getChoiceOptions = (choiceId: number) => {
    const choice = choices.find((c: IChoiceDto) => c.id === choiceId);
    const modifierChoice = modifier.choices.find((c: IModifierChoiceDto) => c.choiceId === choiceId);

    return { choice, modifierChoice };
  };

  const getChoicePrice = (selectedChoice: ISelectedChoice) => {
    const { choice, modifierChoice } = getChoiceOptions(selectedChoice.choiceId);
    return choice && modifierChoice ? getSingleChoicePrice(choice.defaultPrice, modifierChoice.overwritePrice) : 0;
  };

  // We cannot combine includedChoices and freeAmount, so we prioritize freeAmount
  const ignoreIncluced = ignoreIncludedChoices(modifier.choices);

  if (ignoreIncluced) {
    // Modifier has no includedChoices, so we check each choice's free amount
    totalChoicePrice = selectedChoices.reduce((total: number, currentChoice: ISelectedChoice) => {
      let updatedTotal = total;

      const { choice, modifierChoice } = getChoiceOptions(currentChoice.choiceId);

      if (choice && modifierChoice) {
        const extraCount = currentChoice.amount - modifierChoice.freeAmount;
        const extraPrice = extraCount > 0 ? extraCount * getSingleChoicePrice(choice.defaultPrice, modifierChoice.overwritePrice) : 0;
        updatedTotal = total + extraPrice;
      }

      return updatedTotal;
    }, totalChoicePrice);
  } else {
    // Sort by the most expensive choices to include and calculate price for the rest
    const sortedByPrice = selectedChoices.sort((a: ISelectedChoice, b: ISelectedChoice) => getChoicePrice(b) - getChoicePrice(a));

    let includedLeft = choicesIncluded; // Keeping track of how many choices are included and counting down as we loop through
    let includedIndex = 0;

    while (includedIndex < sortedByPrice.length) {
      const singleSelectedChoice = sortedByPrice[includedIndex];

      if (singleSelectedChoice.amount > 0) {
        let amountToCharge = includedLeft > 0 ? 0 : singleSelectedChoice.amount; // How many of these choices are not included in the price

        if (includedLeft > 0 && includedLeft < singleSelectedChoice.amount) {
          // There are still included choices but the amount was higher than the included so we keep the leftover for price calculation
          // amountToCharge = amountToCharge-includedLeft;
          amountToCharge = singleSelectedChoice.amount - includedLeft;
        }

        totalChoicePrice += amountToCharge * getChoicePrice(singleSelectedChoice);
        includedLeft = includedLeft - singleSelectedChoice.amount;
      }

      includedIndex++;
    }
  }
  return totalChoicePrice;
};

/**
 * Filters out modifiers with all choices sold out
 * @param modifiers
 * @param modifierIdsToCheck
 * @param blockedChoiceIds
 */
export function filterModifiersForSoldOutChoices(modifiers: IModifierDto[], modifierIdsToCheck: number[], blockedChoiceIds: number[]) {
  const filteredModifierList: IModifierDto[] = [];
  let modifierIdIndex = 0;

  while (modifierIdIndex < modifierIdsToCheck.length) {
    const singleModifierId = modifierIdsToCheck[modifierIdIndex];
    const modifierToCheck = modifiers.find((m) => m.id === singleModifierId);

    if (modifierToCheck) {
      const updatedModifier = Object.assign({}, modifierToCheck);

      updatedModifier.choices = updatedModifier.choices.filter((choice) => {
        return blockedChoiceIds.find((choiceId) => choice.choiceId === choiceId) === undefined;
      });

      if (updatedModifier.choices.length > 0) {
        filteredModifierList.push(updatedModifier);
      }
    }

    modifierIdIndex++;
  }

  return filteredModifierList;
}
